import React, { useState, useEffect } from "react";
import {
  Plus,
  Bot,
  Edit,
  Trash2,
  TestTube,
  Power,
  MessageCircle,
} from "lucide-react";
import { Agent, AGENT_TYPE_INFO } from "../../types/agent";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { agentApi } from "../../services/agentApi";
import { AgentChatInterface } from "../../components/admin/AgentChatInterface";

export function AgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await agentApi.getAgents();
      setAgents(response.agents);
    } catch (err) {
      setError("Failed to load agents");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = () => {
    setShowCreateForm(true);
    setEditingAgent(null);
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setShowCreateForm(true);
  };

  const handleDeleteAgent = async (agent: Agent) => {
    if (confirm(`Are you sure you want to delete "${agent.name}"?`)) {
      try {
        await agentApi.deleteAgent(agent.id);
        await loadAgents();
      } catch (err) {
        setError("Failed to delete agent");
        console.error(err);
      }
    }
  };

  const handleTestAgent = async (agent: Agent) => {
    try {
      const response = await agentApi.testAgent(agent.id, {
        message: "Hello, this is a test message.",
      });
      alert(`Agent Response: ${response.response}`);
    } catch (err) {
      setError("Failed to test agent");
      console.error(err);
    }
  };

  const toggleAgentStatus = async (agent: Agent) => {
    try {
      await agentApi.updateAgent(agent.id, {
        is_active: !agent.is_active,
      });
      await loadAgents();
    } catch (err) {
      setError("Failed to update agent status");
      console.error(err);
    }
  };

  const handleChatWithAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setShowChatModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading agents...</div>
      </div>
    );
  }

  if (showCreateForm || editingAgent) {
    return (
      <AgentForm
        agent={editingAgent}
        onSave={async (data) => {
          try {
            if (editingAgent) {
              await agentApi.updateAgent(editingAgent.id, data);
            } else {
              await agentApi.createAgent(data);
            }
            setShowCreateForm(false);
            setEditingAgent(null);
            await loadAgents();
          } catch (err) {
            setError("Failed to save agent");
            console.error(err);
          }
        }}
        onCancel={() => {
          setShowCreateForm(false);
          setEditingAgent(null);
        }}
      />
    );
  }

  if (showChatModal && selectedAgent) {
    return (
      <AgentChatInterface
        agent={selectedAgent}
        onClose={() => {
          setShowChatModal(false);
          setSelectedAgent(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agents</h1>
          <p className="text-gray-600">
            Manage and configure AI agents for different tasks
          </p>
        </div>
        <Button onClick={handleCreateAgent} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Agent
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setError(null)}
            className="mt-2"
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <Card key={agent.id} className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">{agent.name}</h3>
              </div>
              <div className="flex items-center gap-1">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    agent.is_active
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {agent.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div>
                <span className="text-sm font-medium text-gray-700">Type:</span>
                <span
                  className={`ml-2 px-2 py-1 text-xs rounded ${
                    AGENT_TYPE_INFO[agent.agent_type]?.color ||
                    "bg-gray-100 text-gray-800"
                  }`}
                >
                  {AGENT_TYPE_INFO[agent.agent_type]?.label || agent.agent_type}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">
                  Model:
                </span>
                <span className="ml-2 text-sm text-gray-600">
                  {agent.model_id.includes("openai.gpt-oss-20b")
                    ? "GPT OSS 20B"
                    : "Unknown Model"}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 line-clamp-3">
                {agent.system_prompt.length > 100
                  ? `${agent.system_prompt.slice(0, 100)}...`
                  : agent.system_prompt}
              </p>
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-100">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditAgent(agent)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestAgent(agent)}
                  className="flex-1"
                >
                  <TestTube className="h-4 w-4 mr-1" />
                  Test
                </Button>
              </div>

              <Button
                variant="primary"
                size="sm"
                onClick={() => handleChatWithAgent(agent)}
                className="w-full bg-blue-600 text-white hover:bg-blue-700"
                disabled={!agent.is_active}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat with Agent
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleAgentStatus(agent)}
                  className={`flex-1 ${
                    agent.is_active ? "text-red-600" : "text-green-600"
                  }`}
                >
                  <Power className="h-4 w-4 mr-1" />
                  {agent.is_active ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteAgent(agent)}
                  className="flex-1 text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {agents.length === 0 && (
        <Card className="p-12 text-center">
          <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No agents found
          </h3>
          <p className="text-gray-600 mb-4">
            Create your first agent to get started with AI-powered
            conversations.
          </p>
          <Button onClick={handleCreateAgent}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Agent
          </Button>
        </Card>
      )}
    </div>
  );
}

// Simplified form component
function AgentForm({
  agent,
  onSave,
  onCancel,
}: {
  agent?: Agent | null;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    name: agent?.name || "",
    agent_type: agent?.agent_type || "general_assistant",
    system_prompt: agent?.system_prompt || "",
    model_id: agent?.model_id || "openai.gpt-oss-20b-1:0",
    is_active: agent?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">
            {agent ? "Edit Agent" : "Create New Agent"}
          </h2>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent Type
            </label>
            <select
              value={formData.agent_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  agent_type: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(AGENT_TYPE_INFO).map(([type, info]) => (
                <option key={type} value={type}>
                  {info.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Model
            </label>
            <select
              value={formData.model_id}
              onChange={(e) =>
                setFormData({ ...formData, model_id: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="openai.gpt-oss-20b-1:0">
                GPT OSS 20B - OpenAI GPT open source model
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              System Prompt
            </label>
            <textarea
              value={formData.system_prompt}
              onChange={(e) =>
                setFormData({ ...formData, system_prompt: e.target.value })
              }
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              placeholder="Enter the system prompt that defines the agent's behavior and capabilities..."
              required
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) =>
                setFormData({ ...formData, is_active: e.target.checked })
              }
              className="mr-2"
            />
            <label
              htmlFor="is_active"
              className="text-sm font-medium text-gray-700"
            >
              Active
            </label>
          </div>

          <div className="flex gap-3 pt-6">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? "Saving..." : agent ? "Update Agent" : "Create Agent"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
